import {
  ClickAwayListener,
  makeStyles,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core'
import classNames from 'classnames'
import { ReportRecognitionIssuesMenuItem } from 'features/BillOfMaterials/BoMItemMenu/MenuItems/ReportRecognitionIssuesMenuItem'
import { BomItemWorkingStepSummary } from 'features/BillOfMaterials/components/BomItemWorkingStepSummary'
import { PartTypeDimensionSummary } from 'features/BillOfMaterials/PartType/PartTypeDimensionSummary'
import { PurchasingItemButton } from 'features/BillOfMaterials/PartType/PurchasingItemButton'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import { WorkingStepStats } from 'features/BillOfMaterials/store/selectors/assemblyWorkingStepsSummaryWithCounterSelector'
import { bomItemNameSelector } from 'features/BillOfMaterials/store/selectors/bomItemNameSelector'
import { bomItemRowNumberSelector } from 'features/BillOfMaterials/store/selectors/bomItemNumberSelector'
import { bomItemIsSearchedSelector } from 'features/BillOfMaterials/store/selectors/bomItemSearched'
import { bomItemWorkingStepsSelector } from 'features/BillOfMaterials/store/selectors/bomItemWorkingStepsSelector'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { BomItemDetailsDrawerProps } from 'features/BomItemDetailsDrawer/BomItemDetailsDrawer'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import { BoMItemType } from 'model/Project/BoMItemRow'
import moment from 'moment'
import { ReactChild, ReactFragment, ReactPortal, useRef, useState } from 'react'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  ProjectOperationDto,
  ProjectOperationStatus,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ProjectViews } from 'store/UI/UITypes'
import { useProjectOperationsStyles } from './ProjectOperationsLogsDrawer.styles'

type Props = {
  operationGroup: ProjectOperationDto
  primaryWorkingStepsFilter?: WorkingStepStats[]
}

const useOperationBoxStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'block',
    width: theme.spacing(2),
    height: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: (props: {
      operationStatus: ProjectOperationStatus
      rowNumber?: number
    }) => {
      switch (props.operationStatus) {
        case ProjectOperationStatus.Failed:
          return theme.palette.error.main
        case ProjectOperationStatus.Started:
          return theme.palette.primary.main
        case ProjectOperationStatus.Finished:
          return props.rowNumber > 0
            ? theme.palette.success.main
            : theme.palette.success.dark
        case ProjectOperationStatus.Queued:
          return theme.palette.grey[200]
        default:
          return theme.palette.grey[200]
      }
    },
  },
  tooltip: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    alignItems: 'center',
    // width: '400px',
  },
  tooltipMenu: {
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
  },
  customMaxWidth: {
    maxWidth: '400px',
  },
  lastItem: {
    marginLeft: 'auto',
  },
  highlight: {
    outline: `3px solid ${theme.palette.info.dark}`,
  },
}))

function OperationPartTypeTooltip(props: {
  tooltip: string
  rowNumber: boolean | ReactChild | ReactFragment | ReactPortal
  bomItemName: boolean | ReactChild | ReactFragment | ReactPortal
  imageSrc: string
  operationGroup: ProjectOperationDto
  onPurchasingPriceClicked?: () => void
}) {
  return (
    <Paper className={props.tooltip} elevation={0}>
      {Boolean(props.rowNumber) && (
        <Typography variant="caption" component="span">
          {props.rowNumber}
        </Typography>
      )}
      <Typography variant="body1" component="span">
        {props.bomItemName}
      </Typography>
      {props.imageSrc && (
        <img
          src={props.imageSrc}
          style={{
            height: '200px',
            width: '200px',
          }}
          loading="lazy"
        />
      )}
      <PartTypeDimensionSummary
        bomItemId={props.operationGroup.boMItemId}
        typographyProps={{
          variant: 'body2',
        }}
      />
      <BomItemWorkingStepSummary bomItemId={props.operationGroup.boMItemId} />
      <Typography variant="caption">
        {props.operationGroup.status} |{' '}
        {moment(props.operationGroup.lastUpdatedAt).format('L HH:mm:ss')}
      </Typography>
      {props.operationGroup.errorMessage && (
        <Typography variant="body2" color="secondary">
          {props.operationGroup.errorMessage}
        </Typography>
      )}
    </Paper>
  )
}

export const OperationBox = (props: Props) => {
  const dispatch = useAppDispatch()

  const { imageSrc } = useBomItemThumbnailImageSrc(
    props.operationGroup.boMItemId
  )
  const bomItemName = useAppSelector(
    bomItemNameSelector(props.operationGroup.boMItemId)
  )
  const rowNumber = useAppSelector(
    bomItemRowNumberSelector(props.operationGroup.boMItemId)
  )

  const bomItemIsSearched = useAppSelector(
    bomItemIsSearchedSelector(props.operationGroup.boMItemId)
  )

  const classes = useOperationBoxStyles({
    operationStatus: props.operationGroup.status,
    rowNumber: rowNumber,
  })

  const bomItemWorkingSteps = useAppSelector(
    bomItemWorkingStepsSelector({
      bomItemId: props.operationGroup.boMItemId,
      includeOwnWorkingSteps: true,
    })
  )

  const boxRef = useRef(undefined)

  const [menuOpen, setMenuOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const { openDialog } = useDialogManager()

  if (
    props.primaryWorkingStepsFilter?.length &&
    bomItemWorkingSteps?.findIndex((x) =>
      props.primaryWorkingStepsFilter
        .map((x) => x.primaryWorkingStep)
        .includes(x.primaryWorkingStep)
    ) === -1
  ) {
    return null
  }

  const handleClick = () => {
    // if (!rowNumber) {
    //   return
    // }

    dispatch(
      newProjectActions.setOpen({
        bomItemId: props.operationGroup.boMItemId,
        isOpen: true,
      })
    )
    dispatch(
      newProjectActions.setBomItemInSearch(props.operationGroup.boMItemId)
    )
    if (!rowNumber) {
      // assembly, go to tree view
      dispatch(
        newProjectActions.changeProjectView(ProjectViews.AssemblyHierarchy)
      )
    } else {
      // part, go to the list view if it is in tree view
      dispatch(
        newProjectActions.changeProjectViewIfInView({
          desiredView: ProjectViews.SalesView,
          ifInView: ProjectViews.AssemblyHierarchy,
        })
      )
    }
  }

  const handleDblClick = () => {
    // if (!rowNumber) {
    //   return
    // }

    dispatch(
      newProjectActions.setOpen({
        bomItemId: props.operationGroup.boMItemId,
        isOpen: true,
      })
    )
    dispatch(
      newProjectActions.setBomItemInSearch(props.operationGroup.boMItemId)
    )
    if (!rowNumber) {
      // assembly, go to tree view
      dispatch(
        newProjectActions.changeProjectView(ProjectViews.AssemblyHierarchy)
      )
    } else {
      // part, go to the list view if it is in tree view
      dispatch(
        newProjectActions.changeProjectViewIfInView({
          desiredView: ProjectViews.SalesView,
          ifInView: ProjectViews.AssemblyHierarchy,
        })
      )
      openDialog('BomItemDetailsDrawer', {
        bomItemId: props.operationGroup.boMItemId,
        bomItemType: BoMItemType.row,
      } as BomItemDetailsDrawerProps)
    }
  }

  return (
    <>
      <Tooltip
        classes={{
          tooltip: classes.customMaxWidth,
        }}
        arrow
        interactive={menuOpen}
        open={tooltipOpen}
        onClose={() => {
          setTooltipOpen(false)
          setMenuOpen(false)
        }}
        title={
          <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
            <Paper elevation={0}>
              {menuOpen && (
                <div className={classes.tooltipMenu}>
                  <PurchasingItemButton
                    bomItemId={props.operationGroup.boMItemId}
                    buttonProps={{
                      size: 'medium',
                    }}
                  />
                  <ReportRecognitionIssuesMenuItem
                    className={classes.lastItem}
                    variant="IconButton"
                    initialMessage={props.operationGroup.errorMessage}
                    bomItemId={props.operationGroup.boMItemId}
                    onClose={() => setMenuOpen(false)}
                    onClick={() => {
                      setTooltipOpen(false)
                      setMenuOpen(false)
                    }}
                  />
                </div>
              )}
              <OperationPartTypeTooltip
                imageSrc={imageSrc}
                bomItemName={bomItemName}
                rowNumber={rowNumber}
                tooltip={classes.tooltip}
                operationGroup={props.operationGroup}
              />
            </Paper>
          </ClickAwayListener>
        }
      >
        <span>
          <div
            ref={boxRef}
            className={classNames(classes.root, {
              [classes.highlight]: bomItemIsSearched,
            })}
            onClick={handleClick}
            onDoubleClick={handleDblClick}
            // style={{ cursor: `${rowNumber ? 'pointer' : 'default'}` }}
            style={{ cursor: `pointer` }}
            onMouseEnter={() => {
              setTooltipOpen(true)
            }}
            onMouseLeave={() => {
              if (!menuOpen) {
                setTooltipOpen(false)
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              handleClick()
              setMenuOpen((current) => !current)
            }}
          />
        </span>
      </Tooltip>
    </>
  )
}

export const OperationGroupChildrensBoxes = (props: Props) => {
  const classes = useProjectOperationsStyles()

  if (!props.operationGroup?.children?.length) {
    return null
  }

  return (
    <ul className={classes.operationBoxContainer}>
      {props.operationGroup.children.map((operation) => {
        return (
          <OperationBox
            key={operation.id}
            operationGroup={operation}
            primaryWorkingStepsFilter={props.primaryWorkingStepsFilter}
          />
        )
      })}
    </ul>
  )
}
